<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import router from '@/router'
import PersonalDetailsEditForm from '@/components/views/manage-account/PersonalDetailsEditForm.vue'
import AuthExplainerModal from '@/components/dashboard/modals/AuthExplainerModal.vue'
import DashboardNavIcon from '@/components/dashboard/shell/DashboardNavIcon.vue'
import { LANDING_PAGE } from '@/types'
import type { Messages } from '@/i18n/en'

const props = withDefaults(
  defineProps<{
    formType?: string
  }>(),
  {
    formType: 'edit',
  }
)

const emit = defineEmits<{
  cancelEdit: []
  saved: []
}>()

const store = useStore()
const { tm } = useI18n<{ message: Messages }>()

const whyModalOpen = ref(false)

const createDetailsPage = computed(() => {
  return (
    router.currentRoute.value.name === 'DONOR_FLOW_ManageAccountCreateDetails'
  )
})

const resolvedFormType = computed(() => {
  if (createDetailsPage.value) {
    return 'create'
  }

  return props.formType
})

const whyParagraphs = computed(() => {
  const paragraphs = tm('LoginRegister.Details.WhyModal.Paragraphs')
  return Array.isArray(paragraphs) ? (paragraphs as string[]) : []
})

const handleCancelEdit = () => {
  emit('cancelEdit')
}

const handleSaved = () => {
  emit('saved')
  if (createDetailsPage.value) {
    store.commit('SET_LANDING_PAGE', LANDING_PAGE.QUESTIONS)
    router.push({ name: 'DONOR_FLOW_ManageAccountCreateDeclaration' })
  } else {
    emit('cancelEdit')
  }
}
</script>

<template>
  <div
    id="account"
    :class="{ 'sa-auth-step sa-auth-step--arrive': createDetailsPage }"
    data-testid="account-section"
  >
    <header v-if="createDetailsPage" class="sa-auth-step__header">
      <div class="sa-auth-step__icon" aria-hidden="true">
        <!-- Same profile glyph as Account nav. -->
        <DashboardNavIcon name="settings" />
      </div>
      <p class="sa-auth-step__eyebrow">
        {{ $t('LoginRegister.Details.Eyebrow') }}
      </p>
      <h1 class="sa-auth-step__title">
        {{ $t('LoginRegister.Details.Header') }}
      </h1>
      <i18n-t
        keypath="LoginRegister.Details.SubHeader"
        tag="p"
        class="sa-auth-step__lead"
        scope="global"
      >
        <template #learnMore>
          <button
            type="button"
            class="sa-auth-step__explain"
            @click="whyModalOpen = true"
          >
            {{ $t('LoginRegister.Details.LearnMore') }}
          </button>
        </template>
      </i18n-t>
    </header>

    <PersonalDetailsEditForm
      :form-type="resolvedFormType"
      @cancel-edit="handleCancelEdit"
      @saved="handleSaved"
    />

    <AuthExplainerModal
      :is-open="whyModalOpen"
      title-id="signup-details-why-title"
      :title="$t('LoginRegister.Details.WhyModal.Title')"
      :paragraphs="whyParagraphs"
      @close="whyModalOpen = false"
    />
  </div>
</template>
